import {
  MW_OBSERVABLE_FIELDS,
  MW_STAGES,
  MW_STAGE_CREDIT,
  mwStageCredit,
  type MwRevealCursor,
  type MwStage,
} from '@badger-sentinel/rules';
import type { CellViewWire } from '@badger-sentinel/protocol';

import { formatClock } from './countdown.js';

/**
 * The Missile Warning board, as pure functions of `CellViewWire` (M5-D8).
 *
 * This module is the whole of the `mw.js` the prototype imported, rebuilt from
 * the wire. Nothing here reads a clock: every countdown takes
 * **server-corrected** milliseconds as an argument, for `useServerNow`'s reason —
 * the MW clock is the most tempting place in the product to start an interval
 * from `Date.now()`, and the defence that holds is that the function which
 * renders the number cannot reach a local one.
 *
 * ## The two nulls, again
 *
 * Phase 3 drew `tn: null`'s two meanings apart in `tnBasisOf`. This phase has
 * the same shape and the same rule, one layer out:
 *
 * | state | wire | drawn as |
 * | --- | --- | --- |
 * | a stage that has not fired | **no observation at all** | no row, and no group |
 * | a data point the constellation dropped | `{stage, field, value: null}` | `—`, in a row that is there |
 *
 * They are separated in `observationGroups` and nowhere else, and the two are
 * asserted **against each other** rather than each alone — a single branch that
 * produced a blank row for both would satisfy either assertion on its own.
 *
 * ## Credit is the stage term and never a total
 *
 * `MW_STAGE_CREDIT` is read, never re-derived (Q27: 4 / 3 / 2 / 0, confirmed).
 * The correctness term is graded against the launch's true model, which this seat
 * does not know and must not be told before the turn resolves (M4-D7) — so the
 * rail shows what waiting costs and never what the call was worth.
 */

export type MwTrackWire = CellViewWire['mwTracks'][number];
export type MwModelWire = CellViewWire['mwCatalogue'][number];
export type MwObservationWire = MwTrackWire['observations'][number];
export type MwReleaseWire = CellViewWire['mwReleases'][number];

/** What has happened to a track, as the rail groups it. */
export type MwTrackState = 'LIVE' | 'RELEASED' | 'DISMISSED';

export function trackStateOf(track: MwTrackWire): MwTrackState {
  if (track.release === null) return 'LIVE';
  return track.release.disposition === 'RELEASE' ? 'RELEASED' : 'DISMISSED';
}

/**
 * The stage credit a decision at this cursor earns — **the stage term alone**.
 *
 * Delegated to `mwStageCredit` rather than indexed here, so the client cannot
 * hold a second opinion about what waiting costs. Stage `0` is worth nothing
 * because nothing has been decided.
 */
export function creditAt(stage: MwRevealCursor): number {
  return mwStageCredit(stage);
}

export function signed(value: number): string {
  return value > 0 ? '+' + String(value) : String(value);
}

// ---------------------------------------------------------------------------
// Stage schedule and the corrected clock
// ---------------------------------------------------------------------------

export interface NextStage {
  readonly stage: number;
  readonly atMs: number;
  readonly inMs: number;
  /** `mm:ss` to arrival, floored at zero. */
  readonly clock: string;
}

/**
 * The next stage due on this track, given **corrected** server time.
 *
 * The schedule the projection sends already excludes stages that have fired, so
 * this is the head of it — except that a stage whose instant has passed but whose
 * snapshot has not landed yet is still the next one, and saying `00:00` while the
 * data is in flight is the truth.
 */
export function nextStageOf(track: MwTrackWire, serverNowMs: number): NextStage | null {
  const entry = [...track.stageSchedule].sort((a, b) => a.atMs - b.atMs)[0];
  if (entry === undefined) return null;
  const inMs = Math.max(0, entry.atMs - serverNowMs);
  return { stage: entry.stage, atMs: entry.atMs, inMs, clock: formatClock(inMs) };
}

/** Every stage still to come, for the timeline dock's lane. */
export interface StageMark {
  readonly stage: MwStage;
  readonly state: 'OBSERVED' | 'PENDING';
  /** Where along the window this stage sits, `0–1`. Null when it has no scheduled time. */
  readonly at: number | null;
}

/**
 * One track's lane on the timeline (design 857–867).
 *
 * The span is the orders window, because that is the only interval every lane
 * shares and the only one the seat is actually racing. A lane scaled to its own
 * last stage would draw two tracks at different speeds.
 *
 * Positions are **computed from the step, not looked up in the schedule.** The
 * projection filters fired stages out of `stageSchedule` — their data is in
 * `observations` and a countdown to something that has arrived is noise — so a
 * lane built by lookup would draw the pending marks and silently omit every one
 * the seat has already been given, which is the half the operator is reading the
 * lane for. A briefed track has no span at all and gets no marks, which is
 * correct: it arrived named and has nothing scheduled.
 */
export function stageMarks(
  track: MwTrackWire,
  span: { readonly fromMs: number; readonly toMs: number; readonly stepMs: number } | null,
): readonly StageMark[] {
  const width = span === null ? 0 : Math.max(1, span.toMs - span.fromMs);
  const scheduled = new Set(track.stageSchedule.map((entry) => entry.stage));
  return MW_STAGES.map((stage) => {
    const observed = stage <= track.revealedThroughStage;
    // A stage this track will never get — a briefed one — is neither observed nor
    // pending on the axis, and is not drawn.
    const placeable = span !== null && (observed || scheduled.has(stage));
    const at = placeable ? Math.max(0, Math.min(1, (stage * span.stepMs) / width)) : null;
    return { stage, state: observed ? 'OBSERVED' : 'PENDING', at };
  });
}

// ---------------------------------------------------------------------------
// Observations — where the two nulls are drawn apart
// ---------------------------------------------------------------------------

export interface ObservationRow {
  readonly field: string;
  readonly label: string;
  /** The reading, or `null` where the constellation dropped it. */
  readonly value: string | null;
}

export interface ObservationGroup {
  readonly stage: MwStage;
  readonly credit: number;
  readonly rows: readonly ObservationRow[];
}

/** RB §5.3.5's column names, as an operator says them. */
export const OBSERVABLE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  originSite: 'ORIGIN SITE',
  irIntensity: 'INFRARED INTENSITY',
  boostDurationS: 'BOOST DURATION',
  stagingEvents: 'STAGING EVENTS',
  apogee: 'APOGEE ESTIMATE',
  groundTrack: 'GROUND TRACK',
  impactSite: 'TRACK TERMINATES',
  terminal: 'TERMINAL BEHAVIOUR',
  rv: 'REENTRY VEHICLES',
});

export function observableLabel(field: string): string {
  return OBSERVABLE_LABELS[field] ?? field.toUpperCase();
}

/**
 * The observations, grouped by the stage that produced them.
 *
 * **Constructed from what arrived, never built whole and filtered** — the same
 * construction `mwObservations` uses server-side, for the same reason: a group
 * built from `MW_OBSERVABLE_FIELDS` and then emptied is a blank row per unfired
 * stage, which is the leak this rule exists to prevent. A stage with no
 * observations contributes no group at all.
 */
export function observationGroups(track: MwTrackWire): readonly ObservationGroup[] {
  const byStage = new Map<MwStage, ObservationRow[]>();
  for (const observation of track.observations) {
    const rows = byStage.get(observation.stage) ?? [];
    rows.push({
      field: observation.field,
      label: observableLabel(observation.field),
      value: observation.value,
    });
    byStage.set(observation.stage, rows);
  }
  return [...byStage.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([stage, rows]) => ({ stage, credit: MW_STAGE_CREDIT[stage], rows }));
}

/**
 * The stage a field belongs to, read from the shared table.
 *
 * Used by the recognition table's observed strip to say *when* a reading is due,
 * so a column with nothing in it can distinguish "stage 3 has not fired" from
 * "the constellation dropped it".
 */
export function stageOfField(field: string): MwStage | null {
  for (const stage of MW_STAGES) {
    const fields: readonly string[] = MW_OBSERVABLE_FIELDS[stage];
    if (fields.includes(field)) return stage;
  }
  return null;
}

/** The last reading for a field, or `undefined` when its stage has not fired. */
export function observationFor(track: MwTrackWire, field: string): MwObservationWire | undefined {
  return [...track.observations].reverse().find((observation) => observation.field === field);
}

// ---------------------------------------------------------------------------
// The launch-event rail
// ---------------------------------------------------------------------------

export type MwSortId = 'NEXT' | 'STAGE' | 'ID';

export interface MwSort {
  readonly id: MwSortId;
  readonly label: string;
}

/**
 * Three sorts, and none of them is the design's `IMPACT`.
 *
 * The prototype sorted by time-to-impact because its mock events carried one.
 * `MwTrackView` does not — impact time is not on the wire and is not derivable —
 * so the binding is deleted rather than defaulted (M5-D10). What the seat can
 * actually race is the *next stage*, and that is the default.
 */
export const MW_SORTS: readonly MwSort[] = Object.freeze([
  { id: 'NEXT', label: 'NEXT DATA' },
  { id: 'STAGE', label: 'STAGE' },
  { id: 'ID', label: 'ID' },
] as const);

export interface MwTrackRow {
  readonly eventId: string;
  readonly state: MwTrackState;
  readonly briefed: boolean;
  readonly open: boolean;
  readonly stage: MwRevealCursor;
  /** The working or released identity, or null while the seat has named nothing. */
  readonly identity: string | null;
  /** The credit a decision *now* would earn. Null once the track is decided. */
  readonly creditNow: number | null;
  /**
   * The credit already banked, once **this seat** decided.
   *
   * Null on a briefed track (M4-D3): it arrived named and disposed of, the seat
   * was never offered the decision, and `creditAt(4)` is a number computed for a
   * term that does not exist. Rendering it as `0` reads as "you earned nothing"
   * — a judgement on a choice nobody made. Absent values render as absent.
   */
  readonly creditBanked: number | null;
  readonly next: NextStage | null;
  readonly pips: readonly StageMark[];
}

export function trackRow(
  track: MwTrackWire,
  serverNowMs: number,
  span: { readonly fromMs: number; readonly toMs: number; readonly stepMs: number } | null,
): MwTrackRow {
  const state = trackStateOf(track);
  return {
    eventId: track.eventId,
    state,
    briefed: track.briefed,
    open: track.open,
    stage: track.revealedThroughStage,
    // The working identity first. On a briefed track the release carries the
    // *disposition* (`BENIGN`) where the model name belongs, so taking the
    // release first puts a verdict in the identity slot and hides the one thing
    // the operator would say out loud.
    identity: track.workingIdentity ?? track.release?.identity ?? null,
    creditNow: state === 'LIVE' ? creditAt(track.revealedThroughStage) : null,
    creditBanked: track.release === null || track.briefed ? null : creditAt(track.release.stage),
    next: nextStageOf(track, serverNowMs),
    pips: stageMarks(track, span),
  };
}

export interface MwGroup {
  readonly id: 'LIVE' | 'CLOSED';
  readonly label: string;
  readonly rows: readonly MwTrackRow[];
}

function compare(sort: MwSortId, a: MwTrackRow, b: MwTrackRow): number {
  switch (sort) {
    case 'NEXT': {
      // A track with no stage left to come is not "soonest"; it goes last.
      const left = a.next?.inMs ?? Number.POSITIVE_INFINITY;
      const right = b.next?.inMs ?? Number.POSITIVE_INFINITY;
      if (left !== right) return left - right;
      break;
    }
    case 'STAGE':
      if (a.stage !== b.stage) return b.stage - a.stage;
      break;
    case 'ID':
      break;
  }
  return a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0;
}

/** The rail's two groups. A group with no rows is not drawn. */
export function trackGroups(rows: readonly MwTrackRow[], sort: MwSortId): readonly MwGroup[] {
  const live = rows.filter((row) => row.state === 'LIVE');
  const closed = rows.filter((row) => row.state !== 'LIVE');
  return [
    { id: 'LIVE' as const, label: 'LIVE', rows: [...live].sort((a, b) => compare(sort, a, b)) },
    {
      id: 'CLOSED' as const,
      label: 'CLOSED',
      rows: [...closed].sort((a, b) => compare(sort, a, b)),
    },
  ].filter((group) => group.rows.length > 0);
}

export interface MwSummary {
  readonly live: number;
  readonly closed: number;
  /**
   * The stage credit banked on **this board**, this window.
   *
   * Not the team's total and not a score: the correctness term is graded against
   * the launch's true model and is withheld until the turn resolves (M4-D7), and
   * VP has no evaluator until M6 (F1). A number nobody can compute is a number
   * that will be wrong the first time somebody checks it, so this one is only
   * what `MW_STAGE_CREDIT` says about the stages this seat decided at.
   */
  readonly credit: number;
}

export function summaryOf(rows: readonly MwTrackRow[]): MwSummary {
  return {
    live: rows.filter((row) => row.state === 'LIVE').length,
    closed: rows.filter((row) => row.state !== 'LIVE').length,
    credit: rows.reduce((total, row) => total + (row.creditBanked ?? 0), 0),
  };
}

/** True while any track on this board still owes a decision — the server's own gate. */
export function hasOpenTracks(view: CellViewWire | null): boolean {
  return view !== null && view.mwTracks.some((track) => track.open);
}

// ---------------------------------------------------------------------------
// The identity the seat asserts
// ---------------------------------------------------------------------------

export interface IdentityOption {
  readonly id: string;
  readonly label: string;
  readonly category: string;
}

/** The catalogue as an identity list, in the order the projection sent it. */
export function identityOptions(catalogue: readonly MwModelWire[]): readonly IdentityOption[] {
  return catalogue.map((model) => ({
    id: model.id,
    label: model.displayName,
    category: model.category,
  }));
}

export function modelById(
  catalogue: readonly MwModelWire[],
  id: string | null,
): MwModelWire | null {
  if (id === null) return null;
  return catalogue.find((model) => model.id === id) ?? null;
}

// Joint Fires' half of a release is **not** here. `stageModifier` and the term it
// is missing are two rows on the INTERCEPT order's ladder (`model/ladder.ts`),
// because that is the moment the number is needed and the only place the seat can
// act on it — and a reader in this module would have been a second opinion about
// what a release says, with one caller.
